import React from 'react';
import { Popup, Button } from 'react-vant';
import { ShareO, Cross } from '@react-vant/icons';
import './ElectronicReport.css';

const ElectronicReport = ({ visible, onClose, studentName, gradeName, className, educationId, record }) => {
    if (!record) return null;

    const formatYear = (yr) => {
        if (!yr) return '';
        if (typeof yr !== 'string') return `${yr}学年`;
        if (yr.includes('学年')) return yr;
        const match = yr.match(/^(\d{4}-\d{4})(.*)$/);
        if (match) {
            return `${match[1]}学年${match[2]}`;
        }
        return `${yr}学年`;
    };

    const gradeMap = {
        'A': '优秀',
        'B': '良好',
        'C': '及格',
        'D': '不及格'
    };
    const displayGrade = gradeMap[record.grade] || record.grade;

    const getGradeColor = (grade) => {
        switch (grade) {
            case 'A': return '#059669'; // 优秀 - 翠绿
            case 'B': return '#0891b2'; // 良好 - 青色
            case 'C': return '#d97706'; // 及格 - 琥珀
            case 'D': return '#e11d48'; // 不及格 - 玫红
            default: return '#94a3b8';
        }
    };
    const gradeColor = getGradeColor(record.grade);

    const ITEM_LABELS = {
        height: '身高',
        weight: '体重',
        bmi: '体重指数(BMI)',
        vital_capacity: '肺活量',
        run_50m: '50米跑',
        sit_and_reach: '坐位体前屈',
        skip_rope: '一分钟跳绳',
        sit_up: '一分钟仰卧起坐',
        run_50m_8x: '50米×8往返跑',
        run_800m: '800米跑',
        run_1000m: '1000米跑',
        pull_up: '引体向上'
    };

    const renderScoreItem = (label, value, unit, score) => (
        <div key={label} className="h5-report-score-item">
            <div className="h5-report-item-label">{label}</div>
            <div className="h5-report-item-value-box">
                <span className="h5-report-item-value">{value || '-'}</span>
                <span className="h5-report-item-unit">{unit}</span>
            </div>
            <div className={`h5-report-item-score-badge ${score >= 90 ? 'excellent' : score >= 80 ? 'good' : 'fair'}`}>
                {score || '-'}分
            </div>
        </div>
    );

    const fullClassName = (() => {
        const g = gradeName || '';
        const c = className || '';
        const full = (c.includes(g) ? c : `${g}${c}`) || '-';
        return full.replace('主校区', '');
    })();

    return (
        <Popup
            visible={visible}
            onClose={onClose}
            position="bottom"
            round
            style={{ height: '90%', background: 'transparent' }}
        >
            <div className="h5-report-overlay">
                <div className="h5-report-container">
                    <div className="h5-report-card">
                        <div className="h5-report-header">
                            <div className="h5-report-header-decor" />
                            <div className="h5-report-title">学年综合素质成长报告单</div>
                            <div className="h5-report-year">{formatYear(record.academic_year || '2023-2024')}</div>
                        </div>

                        <div className="h5-report-student-info">
                            <div className="h5-report-info-col">
                                <div className="h5-report-info-label">姓名</div>
                                <div className="h5-report-info-value">{studentName}</div>
                            </div>
                            <div className="h5-report-info-divider" />
                            <div className="h5-report-info-col">
                                <div className="h5-report-info-label">班级</div>
                                <div className="h5-report-info-value">{fullClassName}</div>
                            </div>
                            <div className="h5-report-info-divider" />
                            <div className="h5-report-info-col">
                                <div className="h5-report-info-label">教育ID</div>
                                <div className="h5-report-info-value">{educationId || '-'}</div>
                            </div>
                        </div>

                        <div className="h5-report-main-score">
                            <div className="h5-report-total-box">
                                <div className="h5-report-total-label">学年综合分</div>
                                <div className="h5-report-total-value">{record.composite_score || record.total_score}</div>
                            </div>

                            {record.bonus_score > 0 && (
                                <div className="h5-report-bonus-box">
                                    <div className="h5-report-bonus-label">附加分</div>
                                    <div className="h5-report-bonus-value">+{record.bonus_score}</div>
                                </div>
                            )}

                            <div className="h5-report-grade-box">
                                <div className="h5-report-grade-circle" style={{ borderColor: gradeColor }}>
                                    <span style={{ color: gradeColor, fontSize: displayGrade.length > 1 ? '16px' : '24px' }}>
                                        {displayGrade}
                                    </span>
                                </div>
                                <div className="h5-report-grade-label">综合评价</div>
                            </div>
                        </div>

                        <div className="h5-report-details">
                            <div className="h5-report-section-title">体测项目明细</div>
                            <div className="h5-report-details-list">
                                <div className="h5-report-score-item">
                                    <div className="h5-report-item-label">体重指数(BMI)</div>
                                    <div className="h5-report-item-value-box">
                                        <span className="h5-report-item-value">
                                            {record.bmi || (record.height && record.weight ? (record.weight / Math.pow(record.height / 100, 2)).toFixed(1) : '-')}
                                        </span>
                                        <span className="h5-report-item-unit" style={{ fontSize: '10px' }}> ({record.height || '-'}cm/{record.weight || '-'}kg)</span>
                                    </div>
                                    <div className={`h5-report-item-score-badge ${(record.bmi_score || record.item_scores?.bmi?.score || 0) >= 90 ? 'excellent' : (record.bmi_score || record.item_scores?.bmi?.score || 0) >= 80 ? 'good' : 'fair'}`}>
                                        {record.bmi_score || record.item_scores?.bmi?.score || '-'}分
                                    </div>
                                </div>

                                {record.item_scores ? (
                                    Object.entries(record.item_scores).map(([key, item]) => {
                                        if (['height', 'weight', 'bmi'].includes(key)) return null;
                                        let displayVal = item.value || record[key] || '-';
                                        let displayUnit = item.unit;
                                        if (['run_50m_8x', 'run50m8x', 'run_800m', 'run800m', 'run_1000m', 'run1000m'].includes(key) && typeof displayVal === 'number') {
                                            const minutes = Math.floor(displayVal);
                                            const secondsStr = displayVal.toFixed(2).split('.')[1];
                                            displayVal = `${minutes}分${secondsStr}秒`;
                                            displayUnit = '';
                                        }
                                        return renderScoreItem(
                                            item.name || ITEM_LABELS[key] || key,
                                            displayVal,
                                            displayUnit,
                                            item.score
                                        );
                                    })
                                ) : (
                                    <>
                                        {renderScoreItem('肺活量', record.vital_capacity, 'ml', record.vital_capacity_score)}
                                        {renderScoreItem('50米跑', record.run_50m, 's', record.run_50m_score)}
                                        {renderScoreItem('坐位体前屈', record.sit_and_reach, 'cm', record.sit_and_reach_score)}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="h5-report-message">
                            <div className="h5-report-message-title">成长寄语</div>
                            <div className="h5-report-message-text">
                                亲爱的{studentName}同学，体育锻炼不仅是强健身心，更是磨炼意志。希望你在未来的学习生活中，继续保持运动的热情，让生命在运动中焕发光彩！
                            </div>
                        </div>

                        <div className="h5-report-footer">
                            <div className="h5-report-footer-text">—— 智慧体育教学支撑平台 ——</div>
                            <div className="h5-report-date">{new Date().toLocaleDateString()}</div>
                        </div>
                    </div>
                </div>

                <div className="h5-report-actions">
                    <Button block round onClick={onClose} className="h5-report-close-btn">
                        返回
                    </Button>
                    <Button block round icon={<ShareO />} type="primary" className="h5-report-share-btn">
                        分享报告
                    </Button>
                </div>
            </div>
        </Popup>
    );
};

export default ElectronicReport;
